// src/components/admin/FlyerSlotsAdmin.jsx
import { useEffect, useState } from "react";
import { supabase } from "../../utils/supabaseClient";
import {
  Box,
  Heading,
  Spinner,
  Text,
  List,
  ListItem,
  Badge,
  Flex,
  Select,
} from "@chakra-ui/react";

/**
 * FlyerSlotsAdmin
 * ADMIN‑ONLY VIEW
 * Shows full slot metadata including:
 * - cancelled
 * - reserved_for
 * - business_id
 * - renewal outcome
 * - slot status
 * - offer metadata
 */
export default function FlyerSlotsAdmin() {
  const [region, setRegion] = useState("PR");
  const [flyers, setFlyers] = useState([]);
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingFlyers, setLoadingFlyers] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Load flyers for region
  useEffect(() => {
    const loadFlyers = async () => {
      setLoadingFlyers(true);

      const { data, error } = await supabase
        .from("flyers")
        .select("id, postcode_sector, season, year, status")
        .eq("region", region)
        .order("postcode_sector", { ascending: true });

      if (!error) {
        setFlyers(data);
        setSelectedFlyer(data?.[0] || null);
      }

      setLoadingFlyers(false);
    };

    loadFlyers();
  }, [region]);

  // Load slots for selected flyer
  useEffect(() => {
    if (!selectedFlyer) return;

    const loadSlots = async () => {
      setLoadingSlots(true);

      const { data, error } = await supabase
        .from("flyer_slots")
        .select(`
          id,
          slot_number,
          status,
          postcode,
          offer_title,
          offer_text,
          business_name,
          business_id,
          cancelled,
          reserved_for,
          qr_code_url,
          token
        `)
        .eq("flyer_id", selectedFlyer.id)
        .order("slot_number", { ascending: true });

      if (!error) setSlots(data || []);

      setLoadingSlots(false);
    };

    loadSlots();
  }, [selectedFlyer]);

  const getColor = (slot) => {
    if (slot.cancelled) return "orange";
    if (slot.status === "branding") return "purple";
    if (slot.status === "sold") return "red";
    if (slot.reserved_for) return "yellow";
    return "green";
  };

  return (
    <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm">
      <Heading size="md" mb={4}>
        🧩 Admin Slot Viewer
      </Heading>

      {/* Region selector */}
      <Flex gap={4} mb={4}>
        <Select
          width="200px"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
        >
          <option value="PR">PR</option>
          <option value="AB">AB</option>
          <option value="BD">BD</option>
          <option value="NW">NW</option>
          <option value="PO">PO</option>
          <option value="SA">SA</option>
          <option value="RM">RM</option>
          <option value="UK">UK</option>
        </Select>

        <Select
          width="260px"
          value={selectedFlyer?.id || ""}
          onChange={(e) =>
            setSelectedFlyer(flyers.find((f) => f.id === e.target.value))
          }
        >
          {flyers.map((f) => (
            <option key={f.id} value={f.id}>
              {f.postcode_sector} — {f.season} {f.year}
            </option>
          ))}
        </Select>
      </Flex>

      {/* Loading states */}
      {loadingFlyers && (
        <Flex align="center" justify="center" p={6}>
          <Spinner size="lg" />
          <Text ml={3}>Loading flyers…</Text>
        </Flex>
      )}

      {loadingSlots && (
        <Flex align="center" justify="center" p={6}>
          <Spinner size="lg" />
          <Text ml={3}>Loading slots…</Text>
        </Flex>
      )}

      {/* Slot list */}
      {!loadingSlots && slots.length > 0 && (
        <List spacing={3}>
          {slots.map((s) => (
            <ListItem
              key={s.id}
              borderBottom="1px solid"
              borderColor="gray.100"
              pb={2}
            >
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontWeight="bold">
                    Slot {s.slot_number} — {s.postcode || "No postcode"}
                  </Text>

                  {s.offer_title && (
                    <Text fontSize="sm" color="gray.600">
                      <em>{s.offer_title}</em>: {s.offer_text}
                    </Text>
                  )}

                  {s.business_name && (
                    <Text fontSize="xs" color="gray.500">
                      Business: {s.business_name} ({s.business_id})
                    </Text>
                  )}

                  {s.cancelled && (
                    <Text fontSize="xs" color="orange.500">
                      Cancelled — will NOT auto‑renew
                    </Text>
                  )}

                  {s.reserved_for && (
                    <Text fontSize="xs" color="yellow.600">
                      Reserved for business: {s.reserved_for}
                    </Text>
                  )}
                </Box>

                <Badge colorScheme={getColor(s)}>{s.status}</Badge>
              </Flex>
            </ListItem>
          ))}
        </List>
      )}

      {!loadingSlots && slots.length === 0 && (
        <Text>No slots found for this flyer.</Text>
      )}
    </Box>
  );
}
